import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;

const LoginOtpStep = ({
  onVerify,
  onResend,
  loading = false,
  resendLoading = false,
  error = "",
  onClearError,
  otpSentAt,
}) => {
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [shake, setShake] = useState(false);
  const [localError, setLocalError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inputRefs = useRef([]);
  const submittedOtpRef = useRef("");

  const displayError = error || localError;
  const hasError = Boolean(displayError);
  const modalBusy = loading || resendLoading;

  const clearErrors = useCallback(() => {
    if (localError) setLocalError("");
    if (error && onClearError) onClearError();
  }, [localError, error, onClearError]);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputRefs.current[0]?.focus();
    });
  }, []);

  useEffect(() => {
    const updateTimer = () => {
      if (!otpSentAt) {
        setSecondsLeft(0);
        return;
      }
      const elapsed = Math.floor((Date.now() - otpSentAt) / 1000);
      setSecondsLeft(Math.max(RESEND_SECONDS - elapsed, 0));
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [otpSentAt]);

  useEffect(() => {
    if (error) {
      triggerShake();
    }
  }, [error, triggerShake]);

  const resendButtonLabel = useMemo(() => {
    if (secondsLeft > 0) {
      return `Resend code in ${secondsLeft}s`;
    }
    return "Resend code";
  }, [secondsLeft]);

  const isResendDisabled = secondsLeft > 0 || resendLoading || loading;

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    clearErrors();

    const newOtpValues = [...otpValues];

    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, OTP_LENGTH);
      for (let i = 0; i < OTP_LENGTH; i++) {
        newOtpValues[i] = digits[i] || "";
      }
      setOtpValues(newOtpValues);
      const nextIndex = Math.min(digits.length, OTP_LENGTH - 1);
      setFocusedIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    if (value && index < OTP_LENGTH - 1) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        setFocusedIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      setFocusedIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    clearErrors();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (pastedData) {
      const newOtpValues = [...otpValues];
      for (let i = 0; i < OTP_LENGTH; i++) {
        newOtpValues[i] = pastedData[i] || "";
      }
      setOtpValues(newOtpValues);
      const nextIndex = Math.min(pastedData.length, OTP_LENGTH - 1);
      setFocusedIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = (otpFromAutoSubmit = "") => {
    const otp = otpFromAutoSubmit || otpValues.join("");
    if (otp.length !== OTP_LENGTH) {
      triggerShake();
      setLocalError("Please enter the complete 6-digit code");
      return;
    }
    if (submittedOtpRef.current === otp && loading) {
      return;
    }
    submittedOtpRef.current = otp;
    clearErrors();
    onVerify(otp);
  };

  const handleResend = async () => {
    if (!onResend || isResendDisabled) return;
    try {
      clearErrors();
      const ok = await onResend();
      if (ok === false) return;
      setOtpValues(Array(OTP_LENGTH).fill(""));
      setFocusedIndex(0);
      submittedOtpRef.current = "";
      requestAnimationFrame(() => inputRefs.current[0]?.focus());
    } catch (err) {
      setLocalError(err?.message || "Failed to resend code");
      triggerShake();
    }
  };

  const otpComplete = otpValues.every((v) => v !== "");
  const joinedOtp = otpValues.join("");

  useEffect(() => {
    if (loading || resendLoading) return;
    if (!otpComplete) {
      submittedOtpRef.current = "";
      return;
    }
    if (submittedOtpRef.current === joinedOtp) return;
    handleSubmit(joinedOtp);
  }, [joinedOtp, otpComplete, loading, resendLoading]);

  return (
    <div className="login-otp-step">
      <h2 className="login-otp-step__title">Verify your identity</h2>
      <p className="login-otp-step__subtitle">
        Enter the 6-digit code sent to your registered email.
      </p>

      <div className="login-otp-step__inputs">
        <div className={`login-otp-step__boxes ${shake ? "shake" : ""}`}>
          {otpValues.map((value, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={value}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={() => {
                setFocusedIndex(index);
                inputRefs.current[index]?.select();
              }}
              className={`login-otp-step__box ${value ? "filled" : ""} ${focusedIndex === index ? "focused" : ""} ${hasError ? "has-error" : ""}`}
              autoComplete="one-time-code"
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              disabled={modalBusy}
            />
          ))}
        </div>

        {hasError ? (
          <div className="login-otp-step__error" role="alert">
            {displayError}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="login-otp-step__submit"
        disabled={modalBusy || !otpComplete}
        onClick={() => handleSubmit()}
      >
        {loading ? "Signing in..." : "Sign In"}
      </button>

      <button
        type="button"
        className={`login-otp-step__resend ${isResendDisabled ? "disabled" : ""}`}
        disabled={isResendDisabled}
        onClick={handleResend}
      >
        {resendLoading ? "Sending..." : resendButtonLabel}
      </button>
    </div>
  );
};

export default LoginOtpStep;
